import { PersonModel } from "@/Domains/Person/Logic/PersonModel";
import { DateFormatHelper, formatName } from "../../Helpers";
import { DetailItem } from "../../Types";
import { AuditModel } from "../Models/AuditModel";


/**
 * =====================================================
 * Class: AuditViewModel
 * =====================================================
 *
 * Description:
 * Wraps an AuditModel to provide formatted audit information
 * for display in the UI. Handles creation, update, deletion,
 * and restoration events, along with associated user names.
 *
 * Responsibilities:
 * - Converts timestamps to local date strings
 * - Formats user full names
 * - Provides an ordered list of audit events for UI detail sections
 *
 * Author: Samano CASTRE
 * Date: February 1, 2026
 */
export class AuditViewModel {
    public creationDate: string = "";
    public updateDate: string = "";
    public deletionDate: string = "";
    public restoreDate: string = "";

    public creatorName: string = "";
    public updaterName: string = "";
    public deleterName: string = "";
    public restorerName: string = "";

    /**
	 * Constructor
	 *
	 * @param model - The AuditModel to wrap
	 * @param isFeminine - Optional flag to adjust event labels for gender
	 *
	 * Behavior:
	 * - Initializes all audit dates and user names
	 */
    public constructor(private model: AuditModel, private isFeminine?: boolean) {
        this.initialize(this.model);
    }

    /**
	 * Initializes the audit view model from the raw model
	 *
	 * @param model - AuditModel
	 *
	 * Behavior:
	 * - Converts dates to local strings
	 * - Extracts full names from associated users
	 */
    public initialize(model: AuditModel) {
        if(!model) return;

        if (model.createdAt) this.creationDate = DateFormatHelper.toLocaleString(model.createdAt);
        if (model.updatedAt) this.updateDate = DateFormatHelper.toLocaleString(model.updatedAt) ?? "";
        if (model.deletedAt) this.deletionDate = DateFormatHelper.toLocaleString(model.deletedAt) ?? "";
        if (model.restoredAt) this.restoreDate = DateFormatHelper.toLocaleString(model.restoredAt) ?? "";

        if (model.createdByUser) this.creatorName = formatName(model.createdByUser?.person);
        if (model.updatedByUser) this.updaterName = formatName(model.updatedByUser?.person);
        if (model.deletedByUser) this.deleterName = formatName(model.deletedByUser?.person);
        if (model.restoredByUser) this.restorerName = formatName(model.restoredByUser?.person);
    }

    /**
	 * Builds an array of audit events with labels, dates, and users
	 *
	 * @returns Array<{key: string, date: string, user: string}>
	 */
    private getEvents() {
        const feminine = this.isFeminine ? "e" : "";
        return [
            { key: 'Créé' + feminine, date: this.creationDate, user: this.creatorName },
            { key: 'Mis' + feminine + ' à jour', date: this.updateDate, user: this.updaterName },
            { key: 'Supprimé' + feminine, date: this.deletionDate, user: this.deleterName },
            { key: 'Restauré' + feminine, date: this.restoreDate, user: this.restorerName },
        ];
    }

    /**
	 * Returns audit events formatted as DetailItem[] for UI
	 *
	 * Behavior:
	 * - Flattens each event into two items: "le" (date) and "par" (user)
	 */
    getAuditTrail = () => {
        return this.getEvents().flatMap(e => [
            { label: `${e.key} le`, value: e.date },
            { label: `${e.key} par`, value: e.user }
        ]) as DetailItem[];
    }
}